// Base types
export interface BaseEntity {
  id: number;
  createdAt?: string;
  updatedAt?: string;
}

// API User response format (as received from backend)
export interface ApiUser extends BaseEntity {
  email: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  phone?: string;
  role: UserRole;
  enabled?: boolean;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
}

// Frontend User format (transformed for frontend usage)
export interface User extends BaseEntity {
  email: string;
  // API field names for compatibility
  first_name: string;
  last_name: string;
  full_name?: string;
  phone?: string;
  // Frontend field names (transformed from API)
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: UserRole;
  enabled?: boolean;
  lastLogin?: string;
  address?: Address;
}

export type UserRole = 'USER' | 'ADMIN';

export interface Address {
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address?: string;
  captchaToken?: string;
}

export interface AuthResponse {
  user: User | null;
  accessToken: string;
  tokenType: string;
}

// Product types
export interface Product extends BaseEntity {
  title: string;
  description: string;
  price: number | null;
  priceEur?: number;
  discountPrice?: number;
  discountPriceEur?: number;
  imageUrl?: string;
  categories?: Category[];
  stock: number;
  isbn?: string;
  author?: string;
  publisher?: string;
  publicationDate?: string;
  publicationYear?: number;
  pages?: number;
  language?: string;
  active: boolean;
  featured?: boolean;
  averageRating?: number;
  reviewCount?: number;
  images?: ProductImage[];
}

export interface ProductImage extends BaseEntity {
  fileName: string;
  fileUrl: string;
  fileType?: string;
  fileSize?: number;
  isPrimary: boolean;
  displayOrder: number;
  altText?: string;
}

export interface Category extends BaseEntity {
  name: string;
  description?: string;
  imageUrl?: string;
  parentCategory?: Category;
  subCategories?: Category[];
}

export interface ProductSearchParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  category?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  minPrice?: number;
  maxPrice?: number;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// Laravel-style paginated response
export interface LaravelPaginatedResponse<T> {
  data: T[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
}

// Backend specific response formats
export interface ProductsResponse {
  products: Product[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
}

export interface UsersResponse {
  users: User[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
}

export interface OrdersResponse {
  orders: Order[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
}

// Cart types
export interface CartItem extends BaseEntity {
  product: Product;
  quantity: number;
  price: number;
}

export interface Cart {
  items: CartItem[];
  totalAmount: number;
  totalItems: number;
}

export interface AddToCartRequest {
  productId: number;
  quantity: number;
}

export interface UpdateCartItemRequest {
  cartItemId: number;
  quantity: number;
}

// Order types
export interface Order extends BaseEntity {
  orderNumber: string;
  user: User | null;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  items: OrderItem[];
  totalAmount: number;
  shippingCost?: number | null;
  status: OrderStatus;
  shippingAddress: ShippingAddress;
  billingAddress?: Address;
  paymentMethod: PaymentMethod;
  notes?: string;
  shippedDate?: string;
  deliveredDate?: string;
}

export interface OrderItem extends BaseEntity {
  product: Product;
  quantity: number;
  price: number;
  totalPrice: number;
}

export type OrderStatus = 
  | 'PENDING' 
  | 'CONFIRMED' 
  | 'PROCESSING' 
  | 'SHIPPED' 
  | 'DELIVERED' 
  | 'CANCELLED'
  | 'RETURNED';

export type PaymentMethod = 'CREDIT_CARD' | 'DEBIT_CARD' | 'PAYPAL' | 'CASH_ON_DELIVERY';

export interface CreateOrderRequest {
  shippingAddress: Address;
  billingAddress?: Address;
  paymentMethod: PaymentMethod;
  notes?: string;
}

// Guest Checkout types
export interface ShippingAddress {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface CartItemDto {
  productId: number;
  quantity: number;
  price: number;
}

export interface GuestCheckoutData {
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  shippingAddress: ShippingAddress;
  cartItems: CartItemDto[];
  paymentMethod: PaymentMethod;
  notes?: string;
}

export interface OrderTrackingRequest {
  email: string;
  orderNumber: string;
}

export interface GuestState {
  guestInfo: {
    name: string;
    email: string;
    phone: string;
  } | null;
  shippingAddress: Address | null;
  paymentMethod: PaymentMethod | null;
  loading: boolean;
  error: string | null;
}

// Admin types
export interface DashboardStats {
  totalUsers: number;
  totalOrders: number;
  totalProducts: number;
  totalRevenue: number;
  recentOrders: Order[];
  topProducts: Product[];
  ordersByStatus: Record<OrderStatus, number>;
  monthlyRevenue: MonthlyRevenue[];
  lowStockProducts: LowStockProduct[];
  conversionRates: ConversionRates;
}

export interface MonthlyRevenue {
  month: string;
  revenue: number;
}

export interface LowStockProduct {
  id: number;
  title: string;
  currentStock: number;
  minStockLevel: number;
  status: 'OUT_OF_STOCK' | 'CRITICAL' | 'LOW' | 'WARNING';
}

export interface ConversionRates {
  totalCustomers: number;
  customersWithOrders: number;
  totalOrders: number;
  completedOrders: number;
  cancelledOrders: number;
  repeatCustomers: number;
  customerToOrderRate: number;
  orderCompletionRate: number;
  repeatCustomerRate: number;
  orderCancellationRate: number;
}

export interface CreateProductRequest {
  title: string;
  description: string;
  price: number;
  discountPrice?: number;
  categoryIds: number[];
  stock: number;
  isbn?: string;
  author?: string;
  publisher?: string;
  publicationDate?: string;
  pages?: number;
  language?: string;
  active?: boolean;
}

export interface UpdateProductRequest extends Partial<CreateProductRequest> {
  id: number;
}

// Redux State types
export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

export interface ProductState {
  products: Product[];
  currentProduct: Product | null;
  categories: Category[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  totalElements: number;
}

export interface CartState {
  items: CartItem[];
  totalAmount: number;
  totalItems: number;
  loading: boolean;
  error: string | null;
}

export interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  loading: boolean;
  error: string | null;
}

export interface ProductStatistics {
  total: number;
  active: number;
  lowStock: number;
  outOfStock: number;
}

export interface AdminState {
  dashboardStats: DashboardStats | null;
  products: Product[];
  orders: Order[];
  users: User[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  totalElements: number;
  productStatistics: ProductStatistics | null;
}

export interface UIState {
  loading: boolean;
  notification: Notification | null;
  modal: Modal;
  drawer: Drawer;
  theme: 'light' | 'dark';
}

export interface Notification {
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration: number;
}

export interface Modal {
  open: boolean;
  type: string | null;
  data: any;
}

export interface Drawer {
  open: boolean;
  type: string | null;
}

export interface CurrencyState {
  currencyInfo: {
    bgnToEurRate: number;
    bgnSymbol: string;
    eurSymbol: string;
  } | null;
  loading: boolean;
  error: string | null;
}

export interface RootState {
  auth: AuthState;
  products: ProductState;
  cart: CartState;
  orders: OrderState;
  admin: AdminState;
  ui: UIState;
  guest: GuestState;
  currency: CurrencyState;
}

// API Response types
export interface ApiError {
  message: string;
  status: number;
  timestamp: string;
  path: string;
}

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

// Component Props types
export interface PageProps {
  children?: React.ReactNode;
  className?: string;
}

export interface FormProps {
  onSubmit: (values: any) => void;
  initialValues?: any;
  validationSchema?: any;
  loading?: boolean;
}

// Common utility types
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

// User transformation utility
export const transformApiUserToFrontend = (apiUser: ApiUser | any): User => {
  return {
    id: apiUser.id,
    email: apiUser.email,
    // Keep API field names
    first_name: apiUser.first_name || '',
    last_name: apiUser.last_name || '',
    full_name: apiUser.full_name,
    phone: apiUser.phone || apiUser.phoneNumber,
    // Add frontend field names (transformed)
    firstName: apiUser.first_name || '',
    lastName: apiUser.last_name || '',
    phoneNumber: apiUser.phone || apiUser.phoneNumber || '',
    role: apiUser.role,
    enabled: apiUser.enabled,
    lastLogin: apiUser.last_login,
    createdAt: apiUser.created_at,
    updatedAt: apiUser.updated_at,
  };
};

// Theme types
export interface CustomTheme {
  palette: {
    primary: {
      main: string;
      light: string;
      dark: string;
    };
    secondary: {
      main: string;
      light: string;
      dark: string;
    };
    background: {
      default: string;
      paper: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
  };
  typography: {
    fontFamily: string;
    h1: any;
    h2: any;
    h3: any;
    h4: any;
    h5: any;
    h6: any;
    body1: any;
    body2: any;
  };
  spacing: (factor: number) => number;
  breakpoints: {
    values: {
      xs: number;
      sm: number;
      md: number;
      lg: number;
      xl: number;
    };
  };
}